import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import "./ModuleSubtopic1.css";
import NavigationButtons from "./components/NavigationButtons";
import { useSubtopicNavigation } from "./hooks/useSubtopicNavigation";

const VideoList = ({ videos, onVideoClick }) => (
  <div className="subtopic-video-list">
    {videos.map((video, index) => (
      <div
        key={index}
        className="subtopic-video-wrapper"
        onClick={() => onVideoClick(video)}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => e.key === "Enter" && onVideoClick(video)}
      >
        <iframe
          src={video}
          allowFullScreen
          title={`Video ${index + 1}`}
          className="subtopic-video-frame"
        ></iframe>
      </div>
    ))}
  </div>
);

const ModuleSubtopic = ({ title, videos, takeaways, questions, exampleVideos, subtopics, subjectPath }) => {
  const [expandedVideo, setExpandedVideo] = useState(null);
  const location = useLocation();

  // Prefer explicit props; fall back to location.state when available
  const stateSubtopics = (location && location.state && location.state.subtopics) || [];
  const stateSubjectPath = (location && location.state && location.state.subjectPath) || '';

  const navSubtopics = (subtopics && subtopics.length > 0) ? subtopics : stateSubtopics;
  // Resolve subject path from props, state, or attempt to infer from the URL
  const inferSubjectPath = (p) => {
    if (!p) return '';
    const path = p.toLowerCase();
    const mapping = {
      'se-': '/subject-SEModules',
      'cns-': '/subject-CNSModules',
      'cvla-': '/subject-CVLAModules',
      'cal-': '/subject-CalculusModules',
      'beee-': '/subject-BEEEModules',
      'toc': '/subject-TOCModules',
      'mpmc-': '/subject-MPMCModules',
      'cn-': '/subject-CNModules',
      'os-': '/subject-OSModules',
      'dbms-': '/subject-DBMSModules',
      'dsa-': '/subject-DSAModules',
      'dm-': '/subject-DMModules',
      'dsd-': '/subject-DSDModules',
      'cd': '/subject-CDModules',
      'ai-': '/subject-AIModules',
      'oop': '/subject-OOPSModules',
      'chem': '/subject-ChemistryModules',
      'cao': '/subject-CAOModules',
      'daa-': '/subject-DAAModules',
      'dbms': '/subject-DBMSModules'
    };

    // look for any key in the path
    for (const key of Object.keys(mapping)) {
      if (path.includes('/' + key) || path.startsWith('/' + key) || path.includes(key)) {
        return mapping[key];
      }
    }
    return '';
  };

  const navSubjectPath = subjectPath || stateSubjectPath || inferSubjectPath(location.pathname);

  const { prevPath, nextPath, handleBack } = useSubtopicNavigation(
    navSubtopics,
    location.pathname,
    navSubjectPath
  );

  return (
    <div className="subtopic-main">
      <div className="subtopic-container">
        <div className="subtopic-header">{title}</div>

        {videos.length > 0 && (
          <div className="subtopic-section">
            <h2>Videos</h2>
            <VideoList videos={videos} onVideoClick={setExpandedVideo} />
          </div>
        )}

        {takeaways.length > 0 && (
          <div className="subtopic-section">
            <h2>Key Takeaways</h2>
            {takeaways.map((item, index) => (
              <div key={index} className="takeaway-item">
                <p>- {item.text}</p>
                {item.image && <img src={item.image} alt={`Takeaway ${index + 1}`} className="takeaway-image" />}
              </div>
            ))}
          </div>
        )}

        {questions.length > 0 && (
          <div className="subtopic-section">
            <h2>Previous Questions</h2>
            {questions.map((question, index) => (
              <p key={index}>- {question}</p>
            ))}
          </div>
        )}

        {exampleVideos.length > 0 && (
          <div className="subtopic-section">
            <h2>Example Question Videos</h2>
            <VideoList videos={exampleVideos} onVideoClick={setExpandedVideo} />
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <NavigationButtons
        prevPath={prevPath}
        nextPath={nextPath}
        onBack={handleBack}
        subjectPath={navSubjectPath}
        subtopics={navSubtopics}
      />

      {/* Video Modal Overlay */}
      {expandedVideo && (
        <div
          className="video-modal-overlay"
          onClick={() => setExpandedVideo(null)}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => e.key === "Escape" && setExpandedVideo(null)}
        >
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="video-modal-close"
              onClick={() => setExpandedVideo(null)}
              aria-label="Close video"
            >
              ✕
            </button>
            <iframe
              src={expandedVideo}
              allowFullScreen
              title="Expanded Video"
              className="video-modal-iframe"
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModuleSubtopic;
